import React, { useMemo, useState } from 'react';

import InventoryFilters from '../../components/website/public/InventoryFilters';
import VehicleCardPublic from '../../components/website/public/VehicleCardPublic';
import { Vehicle } from '../../components/website/WebsiteTemplate';
import { apiService } from '../../services/api';

interface InventoryPageProps {
  title: string;
  emptyState: string;
  detailCta: string;
  contactPhone?: string;
  contactTextPhone?: string;
  contactWhatsappPhone?: string;
  bhphBadgeImageUrl?: string;
  labels: {
    searchPlaceholder: string;
    make: string;
    bodyStyle: string;
    maxPrice: string;
    sortBy: string;
    sortNewest: string;
    sortOldest: string;
    sortPriceLow: string;
    sortPriceHigh: string;
    reset: string;
    allMakes: string;
    allBodyStyles: string;
  };
  vehicles: Vehicle[];
}

const InventoryPage: React.FC<InventoryPageProps> = ({
  title,
  emptyState,
  detailCta,
  contactPhone,
  contactTextPhone,
  contactWhatsappPhone,
  bhphBadgeImageUrl,
  labels,
  vehicles,
}) => {
  const badgeImageUrl = bhphBadgeImageUrl ? apiService.getFileUrl(bhphBadgeImageUrl) : undefined;
  const [search, setSearch] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedBodyStyle, setSelectedBodyStyle] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sortBy, setSortBy] = useState('');

  const makes = useMemo(() => {
    return Array.from(new Set(vehicles.map((vehicle) => vehicle.make).filter(Boolean))).sort();
  }, [vehicles]);

  const bodyStyles = useMemo(() => {
    return Array.from(
      new Set(vehicles.map((vehicle) => vehicle.body_style).filter(Boolean) as string[])
    ).sort();
  }, [vehicles]);

  const filteredVehicles = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const maxPriceNumber = maxPrice ? Number(maxPrice) : null;

    const filtered = vehicles.filter((vehicle) => {
      const searchable = `${vehicle.year} ${vehicle.make} ${vehicle.model}`.toLowerCase();
      const searchMatch = normalizedSearch.length === 0 || searchable.includes(normalizedSearch);

      const makeMatch = selectedMake.length === 0 || vehicle.make === selectedMake;
      const bodyStyleMatch =
        selectedBodyStyle.length === 0 || vehicle.body_style === selectedBodyStyle;

      const price = typeof vehicle.price === 'number' ? vehicle.price : null;
      const priceMatch = maxPriceNumber === null || (price !== null && price <= maxPriceNumber);

      return searchMatch && makeMatch && bodyStyleMatch && priceMatch;
    });

    if (sortBy) {
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'year-desc':
            return (b.year || 0) - (a.year || 0);
          case 'year-asc':
            return (a.year || 0) - (b.year || 0);
          case 'price-asc':
            return (a.price || 0) - (b.price || 0);
          case 'price-desc':
            return (b.price || 0) - (a.price || 0);
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [vehicles, search, selectedMake, selectedBodyStyle, maxPrice, sortBy]);

  const resetFilters = () => {
    setSearch('');
    setSelectedMake('');
    setSelectedBodyStyle('');
    setMaxPrice('');
    setSortBy('');
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{title}</h1>

      <InventoryFilters
        makes={makes}
        bodyStyles={bodyStyles}
        selectedMake={selectedMake}
        selectedBodyStyle={selectedBodyStyle}
        maxPrice={maxPrice}
        search={search}
        sortBy={sortBy}
        labels={labels}
        onSearchChange={setSearch}
        onMakeChange={setSelectedMake}
        onBodyStyleChange={setSelectedBodyStyle}
        onMaxPriceChange={setMaxPrice}
        onSortChange={setSortBy}
        onReset={resetFilters}
      />

      {filteredVehicles.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-gray-700">
          {emptyState}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVehicles.map((vehicle) => (
            <VehicleCardPublic
              key={vehicle.id}
              vehicle={vehicle}
              ctaLabel={detailCta}
              callPhone={contactPhone}
              textPhone={contactTextPhone || contactPhone}
              whatsappPhone={contactWhatsappPhone || contactTextPhone || contactPhone}
              badgeImageUrl={badgeImageUrl}
            />
          ))}
        </div>
      )}
    </main>
  );
};

export default InventoryPage;
